import { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import Flex from '@/components/common/Flex';
import {
  companyInfo,
  invoiceWatermark,
  mizfaLogo,
  numberFormat,
  ui,
} from '../constants';
import { usePricingTariffs } from '../context/PricingTariffsContext';
import { formatRial, formatToman } from '../utils';
import { resolveInvoiceBuyer } from '../utils/invoiceBuyer';
import { calcLineTax } from '@/app/Purchase/utils/introCode';
import type { InvoiceData } from '../types';

interface InvoiceModalProps {
  invoice: InvoiceData;
  onClose: () => void;
}

function InvoiceModal({ invoice, onClose }: InvoiceModalProps) {
  const { realAndLegal } = usePricingTariffs();
  const buyer = resolveInvoiceBuyer(realAndLegal, ui.invoice.buyer);

  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', closeOnEscape);
    document.body.classList.add('modal-open');

    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.classList.remove('modal-open');
    };
  }, [onClose]);

  const today = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  function getItemDiscount(item: InvoiceData['items'][number]): number {
    return item.discount && item.discount > 0 ? item.discount : 0;
  }

  function getItemVat(item: InvoiceData['items'][number]): number {
    return calcLineTax(Math.max(item.amount - getItemDiscount(item), 0));
  }

  const itemsDiscountTotal = invoice.items.reduce(
    (sum, item) => sum + getItemDiscount(item),
    0,
  );
  const itemsVatTotal = invoice.items.reduce(
    (sum, item) => sum + getItemVat(item),
    0,
  );

  return createPortal(
    <div
      className="invoice-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="invoice-shell">
        <Flex className="invoice-toolbar d-print-none gap-2 mb-2 justify-content-center">
          <Button variant="falcon-primary" onClick={() => window.print()}>
            {ui.invoice.printButton}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {ui.invoice.closeButton}
          </Button>
        </Flex>

        <section
          className="invoice-paper rounded-0 bg-white fs--1 text-1100"
          data-bs-theme="light"
          onContextMenu={(event) => event.preventDefault()}
        >
          <img className="invoice-watermark" src={invoiceWatermark} alt="" />
          <header className="invoice-header">
            <div className="invoice-meta text-1000 fw-bold">
              <span>{ui.invoice.dateLabel}: {today}</span>
            </div>
            <h2 className="text-1100 fs-0 mb-0">{ui.invoice.title}</h2>
            <img className="invoice-logo" src={mizfaLogo} alt="میزفا تولز" />
          </header>

          <div className="invoice-section-title rounded-1 text-1100 fw-black border border-1100">
            {ui.invoice.sellerSection}
          </div>
          <div className="invoice-info-grid text-1100">
            <span>{ui.invoice.fields.legalName}: {companyInfo.name}</span>
            <span>{ui.invoice.fields.economicCode}: {companyInfo.economicCode}</span>
            <span>{ui.invoice.fields.registrationNumber}: {companyInfo.registrationNumber}</span>
            <span>{ui.invoice.fields.nationalId}: {companyInfo.nationalId}</span>
            <span>{ui.invoice.fields.postalCode}: {companyInfo.postalCode}</span>
            <span>{ui.invoice.fields.phone}: {companyInfo.phone}</span>
            <span className="wide">{ui.invoice.fields.address}: {companyInfo.address}</span>
          </div>

          <div className="invoice-section-title rounded-1 text-1100 fw-black border border-1100">
            {ui.invoice.buyerSection}
          </div>
          <div className="invoice-info-grid text-1100">
            <span>{ui.invoice.fields.buyerName}: {buyer.name}</span>
            <span>{ui.invoice.fields.economicCode}: {buyer.economicCode}</span>
            <span>{ui.invoice.fields.nationalId}: {buyer.nationalId}</span>
            <span>{ui.invoice.fields.postalCode}: {buyer.postalCode}</span>
            <span className="wide">{ui.invoice.fields.address}: {buyer.address}</span>
          </div>

          <div className="invoice-section-title rounded-1 text-1100 fw-black border border-1100">
            {ui.invoice.itemsSection}
          </div>
          <table className="invoice-table table table-sm table-bordered border-1100 mb-0">
            <thead>
              <tr>
                {ui.invoice.tableHeaders.map((header) => (
                  <th key={header} className="text-1100">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={`${item.label}-${index}`}>
                  <td className="text-1100">{numberFormat.format(index + 1)}</td>
                  <td className="text-1100">{item.label}</td>
                  <td className="text-1100">{formatRial(item.amount)}</td>
                  <td className="text-1100">
                    {getItemDiscount(item) > 0
                      ? formatRial(getItemDiscount(item))
                      : '—'}
                  </td>
                  <td className="text-1100">{formatRial(getItemVat(item))}</td>
                </tr>
              ))}
              <tr className="invoice-subtotal-row">
                <td className="text-1100" colSpan={2}>
                  {ui.invoice.serviceSubtotalLabel}
                </td>
                <td className="text-1100">{formatRial(invoice.serviceSubtotal)}</td>
                <td className="text-1100">
                  {itemsDiscountTotal > 0 ? formatRial(itemsDiscountTotal) : '—'}
                </td>
                <td className="text-1100">{formatRial(itemsVatTotal)}</td>
              </tr>
              {invoice.previousPlanBalance > 0 ? (
                <tr>
                  <td className="text-1100" colSpan={2}>
                    {ui.invoice.previousPlanDeductionLabel}
                  </td>
                  <td className="text-1100">
                    − {formatToman(invoice.previousPlanBalance)}
                  </td>
                  <td className="text-1100" />
                  <td className="text-1100" />
                </tr>
              ) : null}
              {invoice.introCodeWalletCredit > 0 ? (
                <tr>
                  <td className="text-1100" colSpan={2}>
                    {invoice.introCodeDeductionLabel ??
                      ui.invoice.introCodeWalletCreditLabel}
                  </td>
                  <td className="text-1100">
                    + {formatRial(invoice.introCodeWalletCredit)}
                  </td>
                  <td className="text-1100" />
                  <td className="text-1100" />
                </tr>
              ) : null}
              {invoice.walletDeduction > 0 ? (
                <tr>
                  <td className="text-1100" colSpan={2}>
                    {ui.invoice.walletDeductionLabel}
                  </td>
                  <td className="text-1100">
                    {numberFormat.format(invoice.walletDeduction * 10)} -{' '}
                    {ui.common.rial}
                  </td>
                  <td className="text-1100" />
                  <td className="text-1100" />
                </tr>
              ) : null}
              <tr className="invoice-total-row">
                <td className="text-1100 fw-black" colSpan={2}>
                  {ui.invoice.totalLabel}
                </td>
                <td className="text-1100 fw-black" colSpan={3}>
                  {formatRial(invoice.total)}
                </td>
              </tr>
            </tbody>
          </table>

          <footer className="invoice-footer text-1000 border border-1100 border-top-0">
            <span className="invoice-footer-note fw-bold">
              <span>{ui.invoice.validityText}</span>
              <span className="fw-normal">{ui.invoice.postPaymentNote}</span>
            </span>
            <span className="invoice-footer-iban fw-bold border-start border-1100">
              {ui.invoice.ibanLabel}: {companyInfo.iban}
            </span>
          </footer>
        </section>
      </div>
    </div>,
    document.body,
  );
}

export default InvoiceModal;
